/**
 * A send button which waits until there is something worth sending.
 *
 * The browser already knows whether a form is fit to send - every field
 * carries what it requires - but it keeps the answer to itself until the
 * button is pressed, and then answers with a bubble. A form marked
 * `data-ready` asks the same question after every keystroke instead, and
 * greys its own button until the answer is yes.
 *
 * It is opt in rather than automatic. A required field inside a collapsible
 * which is closed makes a form invalid without showing anybody why, and a
 * button greyed for a reason nobody can see is worse than a bubble.
 *
 * A form which is also guarded against leaving unsaved work has two reasons
 * to keep its button out of reach: nothing typed yet, and nothing changed
 * yet. Both are decided here, so the two guards cannot take turns switching
 * the same button on and off.
 */

import { snapshot } from "./unsaved";
import { queryAll } from "../core/dom";

/** Marks a form whose button waits for the fields to be in order. */
export const READY = "data-ready";

/** Return the buttons of `form` which actually send it. */
function sendersOf(form: HTMLFormElement): HTMLButtonElement[] {
    // A button carrying its own address is not sending this form; it is one
    // press doing one thing somewhere else, and it works either way.
    return queryAll<HTMLButtonElement>("button[type=submit]", form).filter(
        // A button which belongs to another form by its `form` attribute only
        // stands inside this one; it sends that other form.
        (button) => !button.hasAttribute("formaction") && button.form === form,
    );
}

/** Keep the send button of one form in step with what is in it. */
export function bindReady(form: HTMLFormElement): void {
    const senders = sendersOf(form);
    if (senders.length === 0) {
        return;
    }

    const guarded = form.hasAttribute("data-guard-unsaved");
    const clean = guarded ? snapshot(form) : "";

    const follow = (): void => {
        const ready = form.checkValidity() && (!guarded || snapshot(form) !== clean);

        for (const button of senders) {
            button.disabled = !ready;
        }
    };

    form.addEventListener("input", follow);
    form.addEventListener("change", follow);
    follow();
}

/** Wire every form which waits for its fields. */
export function initReady(): void {
    for (const form of queryAll<HTMLFormElement>(`form[${READY}]`)) {
        bindReady(form);
    }
}
